import { useState } from 'react';
import { useCreateMailbox, useDomainCheck, useTenantDomains } from '../../api/email';
import { errorMessage } from '../../api/client';
import type { EmailDomain } from '../../api/types';
import { can, useMe } from '../../auth/AuthContext';
import { Alert, Badge, Button, Card, PageHeader, formatDate } from '../../components/ui';
import { EmptyState, ErrorState, SkeletonTable } from '../../components/states';
import {
  Advisories,
  checkSummary,
  CreateMailboxForm,
  DnsTodo,
  MailboxesPanel,
} from '../../components/email/EmailDomain';

interface Notice {
  kind: 'success' | 'info' | 'error';
  text: string;
}

/**
 * Tenant portal: the organisation's email domains.
 *
 * Every domain shows what it still needs, read live from the mail server and
 * the DNS zone. Balsam never writes DNS from here — the records are a list to
 * copy into your DNS — and adding the domain to the mail server is a step
 * Balsam does by hand. Owners create mailboxes; nobody here deletes one.
 */
export function Email() {
  const me = useMe();
  const [notice, setNotice] = useState<Notice | null>(null);
  const domains = useTenantDomains(me.principal.tenantId);

  const notify = (text: string, kind: Notice['kind'] = 'success'): void => {
    setNotice({ kind, text });
  };

  return (
    <>
      <PageHeader
        eyebrow="Portal"
        title="Email"
        description="Your domains on Balsam's mail platform, and what each one still needs."
      />
      {notice ? <Alert kind={notice.kind}>{notice.text}</Alert> : null}
      {domains.isError ? (
        <ErrorState
          error={domains.error}
          onRetry={() => domains.refetch()}
          retrying={domains.isFetching}
        />
      ) : null}
      {domains.isPending ? (
        <Card title="Email domains">
          <SkeletonTable rows={2} cols={3} />
        </Card>
      ) : null}
      {domains.data && domains.data.length === 0 ? (
        <EmptyState
          title="No email domain yet"
          body="Ask your Balsam contact to assign your company domain; it appears here once it is set up."
        />
      ) : null}
      {domains.data?.map((domain) => (
        <DomainCard
          key={domain.id}
          tenantId={me.principal.tenantId as string}
          domain={domain}
          canManage={can(me, 'tenant:domains:manage')}
          onChanged={notify}
        />
      ))}
    </>
  );
}

function DomainCard({
  tenantId,
  domain,
  canManage,
  onChanged,
}: {
  tenantId: string;
  domain: EmailDomain;
  canManage: boolean;
  onChanged: (text: string, kind?: Notice['kind']) => void;
}) {
  const check = useDomainCheck(tenantId, domain.domain);
  const create = useCreateMailbox(tenantId);

  return (
    <Card
      title={domain.domain}
      subtitle={
        check.data ? (
          <>
            <Badge tone={checkSummary(check.data).tone}>{checkSummary(check.data).label}</Badge>{' '}
            <span className="muted">checked {formatDate(check.data.checkedAt)}</span>
          </>
        ) : undefined
      }
      actions={
        <Button variant="secondary" loading={check.isFetching} onClick={() => check.refetch()}>
          Check again
        </Button>
      }
    >
      {check.isError ? <Alert kind="error">{errorMessage(check.error)}</Alert> : null}
      {check.isPending ? <SkeletonTable rows={3} cols={4} /> : null}
      {check.data ? (
        <>
          <p className="muted">{checkSummary(check.data).hint}</p>
          <DnsTodo check={check.data} />
          <Advisories check={check.data} />
          <MailboxesPanel check={check.data}>
            {canManage ? (
              <CreateMailboxForm
                domain={domain.domain}
                onCreate={(input) => create.mutateAsync({ ...input, domain: domain.domain })}
                onChanged={onChanged}
              />
            ) : (
              <p className="muted">Only the owner can create mailboxes.</p>
            )}
          </MailboxesPanel>
        </>
      ) : null}
    </Card>
  );
}
